import { incidentCode } from '../domain/incidentProfiles';
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { Crosshair, Layers, Minus, Plus, Building2, Navigation, Route, Globe2 } from 'lucide-react';
import type { Point, SimState, Vehicle } from '../domain/model';
import { isOpen, incidentLabels } from '../domain/model';
import { vehicleDefinitions } from '../domain/organizations';
import type { CatalogEntry } from '../domain/catalog';
import { request } from '../api';
import { journeyPosition } from '../providers/routing';
import { displaySimulationTime } from '../providers/vehicleMotion';
import { VehicleMapLayer } from './vehicleMapLayer';
import { aerial, type MapMode, type MapProvider } from '../providers/maps';
export interface MapFocus {
  point: Point;
  sequence: number;
  zoom?: number;
}
export const vehicleCategory = (v: Vehicle) => vehicleDefinitions[v.kind].category;
// The two map symbols use the same Lucide geometry as the UI, without a server renderer.
const stationSymbol =
  '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2h-2M10 6h4M10 10h4M10 14h4M10 22v-4h4v4"/></svg>';
const incidentSymbol =
  '<svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z M12 9v4 M12 17h.01"/></svg>';
const colors = {
  fire: '#f05261',
  ambulance: '#ffbb55',
  police: '#52a7ef',
  technical: '#9bb5d1',
};
const placeLabels: { name: string; point: Point; major?: boolean }[] = [
  { name: 'Stuttgart', point: [48.777, 9.182], major: true },
  { name: 'Bad Cannstatt', point: [48.81, 9.214] },
  { name: 'Feuerbach', point: [48.81, 9.151] },
  { name: 'Stuttgart-West', point: [48.778, 9.151] },
  { name: 'Degerloch', point: [48.751, 9.169] },
  { name: 'Stuttgart-Ost', point: [48.783, 9.209] },
  { name: 'Untertürkheim', point: [48.781, 9.253] },
  { name: 'Sillenbuch', point: [48.748, 9.218] },
];
function textElement(tag: string, text: string, className?: string) {
  const el = document.createElement(tag);
  el.textContent = text;
  if (className) el.className = className;
  return el;
}
export default function DispatchMap({
  state,
  selectedId,
  onSelect,
  provider,
  focus,
  onVehicleSelect,
  catalogVisible,
  onCatalogToggle,
  onCatalogSelect,
  onCreateIncident,
}: {
  state: SimState;
  selectedId: string;
  onSelect: (id: string) => void;
  provider: MapProvider;
  focus: MapFocus | null;
  onVehicleSelect: (id?: string) => void;
  catalogVisible: boolean;
  onCatalogToggle: () => void;
  onCatalogSelect: (entry: CatalogEntry) => void;
  onCreateIncident?: (point: Point) => void;
}) {
  const host = useRef<HTMLDivElement>(null),
    map = useRef<L.Map | null>(null),
    markers = useRef<L.LayerGroup | null>(null),
    vehicles = useRef<VehicleMapLayer | null>(null),
    motion = useRef({ state, receivedAt: performance.now() }),
    labels = useRef<L.LayerGroup | null>(null),
    areaLayer = useRef<L.LayerGroup | null>(null),
    catalogLayer = useRef<L.LayerGroup | null>(null),
    catalogSelectRef = useRef(onCatalogSelect),
    createRef = useRef(onCreateIncident),
    selectedRef = useRef(onSelect),
    vehicleSelectRef = useRef(onVehicleSelect);
  catalogSelectRef.current = onCatalogSelect;
  createRef.current = onCreateIncident;
  selectedRef.current = onSelect;
  vehicleSelectRef.current = onVehicleSelect;
  const [bounds, setBounds] = useState<{
      south: number;
      north: number;
      west: number;
      east: number;
      zoom: number;
    } | null>(null),
    [catalogCount, setCatalogCount] = useState(0),
    [catalogError, setCatalogError] = useState('');
  const [mode, setMode] = useState<MapMode>(state.area.name === 'Stuttgart' ? 'aerial' : 'street'),
    [failure, setFailure] = useState(false),
    [loading, setLoading] = useState(true),
    [stationsVisible, setStationsVisible] = useState(false),
    [routesVisible, setRoutesVisible] = useState(true),
    [layerMenu, setLayerMenu] = useState(false);
  useEffect(() => {
    const previous = motion.current;
    const sameClock =
      previous.state.now === state.now &&
      previous.state.running === state.running &&
      previous.state.speed === state.speed;
    motion.current = {
      state,
      receivedAt: sameClock ? previous.receivedAt : performance.now(),
    };
  }, [state]);
  useEffect(() => {
    if (!host.current) return;
    const m = L.map(host.current, {
      zoomControl: false,
      fadeAnimation: false,
      attributionControl: true,
      minZoom: 5,
      maxZoom: 19,
    }).setView(state.area.center, 12);
    map.current = m;
    m.attributionControl.addAttribution(
      'Routen: <a href="https://project-osrm.org/" target="_blank" rel="noreferrer">OSRM</a> · © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> · <a href="https://www.openstreetmap.org/fixthemap" target="_blank" rel="noreferrer">Karte verbessern</a>',
    );
    markers.current = L.layerGroup().addTo(m);
    vehicles.current = new VehicleMapLayer(m, (id) => {
      const vehicle = motion.current.state.vehicles.find((v) => v.id === id);
      if (vehicle?.incidentId) selectedRef.current(vehicle.incidentId);
      else vehicleSelectRef.current(id);
    });
    let frame = 0;
    const animate = (at: number) => {
      const snapshot = motion.current;
      vehicles.current?.render(displaySimulationTime(snapshot.state, snapshot.receivedAt, at));
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    labels.current = L.layerGroup().addTo(m);
    areaLayer.current = L.layerGroup().addTo(m);
    catalogLayer.current = L.layerGroup().addTo(m);
    const recordBounds = () => {
      const b = m.getBounds();
      setBounds({
        south: b.getSouth(),
        north: b.getNorth(),
        west: b.getWest(),
        east: b.getEast(),
        zoom: m.getZoom(),
      });
      if (
        m.getZoom() < 10 ||
        m.getCenter().lat < 47.5 ||
        m.getCenter().lat > 49.9 ||
        m.getCenter().lng < 7.4 ||
        m.getCenter().lng > 10.6
      )
        setMode('street');
    };
    m.on('moveend', recordBounds);
    m.on('click', (e: L.LeafletMouseEvent) => {
      if (!createRef.current) return;
      const content = document.createElement('div');
      content.className = 'map-create-incident';
      content.append(textElement('strong', 'Einsatz an dieser Position'));
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button primary';
      button.textContent = 'Einsatz erstellen';
      button.addEventListener('click', (event) => {
        L.DomEvent.stopPropagation(event);
        m.closePopup();
        createRef.current?.([e.latlng.lat, e.latlng.lng]);
      });
      content.append(button);
      L.popup().setLatLng(e.latlng).setContent(content).openOn(m);
    });
    m.on('contextmenu', (e: L.LeafletMouseEvent) =>
      createRef.current?.([e.latlng.lat, e.latlng.lng]),
    );
    recordBounds();
    m.attributionControl.setPrefix(false);
    const observer = new ResizeObserver(() => m.invalidateSize());
    observer.observe(host.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      vehicles.current?.remove();
      vehicles.current = null;
      m.remove();
      map.current = null;
    };
  }, []);
  useEffect(() => {
    if (!map.current) return;
    const p = mode === 'aerial' ? aerial : provider;
    setFailure(false);
    setLoading(true);
    const layer = L.tileLayer(p.url, {
      attribution: p.attribution,
      maxZoom: p.maxZoom ?? 19,
      keepBuffer: 1,
      updateWhenIdle: true,
    }).addTo(map.current);
    let successes = 0,
      errors = 0;
    layer.on('loading', () => {
      successes = 0;
      errors = 0;
      setLoading(true);
    });
    layer.on('tileerror', () => {
      errors++;
      if (errors >= 3 && !successes) setFailure(true);
    });
    layer.on('tileload', () => {
      successes++;
      setFailure(false);
    });
    layer.on('load', () => {
      setLoading(false);
      setFailure(successes === 0 && errors > 0);
    });
    return () => {
      layer.remove();
    };
  }, [mode, provider]);
  useEffect(() => {
    const layer = labels.current;
    if (!layer) return;
    layer.clearLayers();
    if (mode !== 'aerial') return;
    for (const place of placeLabels) {
      const el = textElement('span', place.name);
      L.marker(place.point, {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: `place-label ${place.major ? 'major' : ''}`,
          html: el,
          iconSize: [160, 24],
          iconAnchor: [80, 12],
        }),
        zIndexOffset: -500,
      }).addTo(layer);
    }
  }, [mode]);
  useEffect(() => {
    const layer = areaLayer.current,
      m = map.current;
    if (!layer || !m) return;
    layer.clearLayers();
    const attribution = 'Gebietsgrenzen © BKG (2026), dl-de/by-2-0';
    if (state.area.boundary?.length) {
      L.polygon(state.area.boundary, {
        color: '#55b8e8',
        weight: 2,
        opacity: 0.9,
        fillColor: '#2f91bd',
        fillOpacity: 0.08,
        dashArray: '7 5',
        interactive: true,
      })
        .bindTooltip(textElement('span', `Zuständigkeitsbereich · ${state.area.name}`), {
          sticky: true,
          className: 'area-map-label',
        })
        .addTo(layer);
      m.attributionControl.addAttribution(attribution);
    }
    return () => {
      layer.clearLayers();
      m.attributionControl.removeAttribution(attribution);
    };
  }, [state.area.controlCenterId]);
  useEffect(() => {
    const layer = markers.current,
      m = map.current;
    if (!layer || !m) return;
    layer.clearLayers();
    if (stationsVisible)
      for (const w of state.stations) {
        L.marker(w.location, {
          title: w.name,
          icon: L.divIcon({
            className: 'station-marker',
            html: stationSymbol,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
          }),
        })
          .bindTooltip(textElement('span', w.name))
          .addTo(layer);
      }
    const openIncidents = state.incidents.filter(isOpen);
    for (const i of openIncidents) {
      const samePlace = openIncidents.filter(
        (other) => other.location[0] === i.location[0] && other.location[1] === i.location[1],
      );
      const incidentOffset =
        m.getZoom() < 10 ? 0 : samePlace.findIndex((other) => other.id === i.id) * 66;
      const selected = i.id === selectedId;
      const marker = L.marker(i.location, {
        title: `E ${i.number} · ${i.title}`,
        icon: L.divIcon({
          className: `incident-marker ${i.type} ${selected ? 'selected' : ''}`,
          html: incidentSymbol,
          iconSize: [38, 38],
          iconAnchor: [19, 19 - incidentOffset],
        }),
        zIndexOffset: selected ? 900 : 700,
      });
      const info = document.createElement('div');
      info.append(
        textElement('b', `${incidentCode(i)}${samePlace.length > 1 ? ` · E ${i.number}` : ''}`),
        textElement('span', i.title),
      );
      marker
        .bindTooltip(info, {
          permanent: m.getZoom() >= 10,
          direction: 'right',
          offset: [21, incidentOffset],
          className: `incident-map-label ${selected ? 'selected' : ''}`,
        })
        .on('click', () => selectedRef.current(i.id))
        .addTo(layer);
    }
    if (m.getZoom() < 10) return;
    for (const v of state.vehicles.filter((v) => v.journey)) {
      const color = colors[vehicleCategory(v)];
      const progress = v.journey ? journeyPosition(v.journey, state.now) : null;
      const route = v.journey?.routeStatus === 'ready' ? v.journey.geometry : undefined;
      if (
        route &&
        progress &&
        routesVisible &&
        (v.incidentId === selectedId || v.journey?.returning)
      ) {
        const travelled = [...route.slice(0, progress.segment + 1), progress.position];
        const remaining = [progress.position, ...route.slice(progress.segment + 1)];
        L.polyline(travelled, {
          color,
          weight: 6,
          opacity: 0.16,
          interactive: false,
        }).addTo(layer);
        L.polyline(travelled, {
          color,
          weight: 2,
          opacity: 0.7,
          interactive: false,
        }).addTo(layer);
        L.polyline(remaining, {
          color,
          weight: 2,
          opacity: 0.65,
          dashArray: '5 6',
          interactive: false,
        }).addTo(layer);
      }
    }
  }, [state, selectedId, stationsVisible, routesVisible, bounds?.zoom]);
  useEffect(() => {
    if (!map.current || !vehicles.current) return;
    vehicles.current.sync(state, map.current.getZoom() >= 10);
    vehicles.current.render(
      displaySimulationTime(state, motion.current.receivedAt, performance.now()),
    );
  }, [state, bounds?.zoom]);
  useEffect(() => {
    if (!map.current) return;
    if (state.area.boundary?.length)
      map.current.fitBounds(L.latLngBounds(state.area.boundary.flat()), {
        padding: [28, 28],
        maxZoom: 11,
      });
    else map.current.setView(state.area.center, 12);
  }, [state.area.controlCenterId, state.area.center[0], state.area.center[1]]);
  useEffect(() => {
    if (focus)
      map.current?.setView(focus.point, focus.zoom ?? Math.max(14, map.current.getZoom()), {
        animate: true,
      });
  }, [focus]);
  useEffect(() => {
    const layer = catalogLayer.current,
      m = map.current;
    if (!layer || !m) return;
    layer.clearLayers();
    setCatalogError('');
    if (!catalogVisible || !bounds) return;
    let cancelled = false;
    const params = new URLSearchParams(Object.entries(bounds).map(([k, v]) => [k, String(v)]));
    request<{
      total: number;
      entries: CatalogEntry[];
      clusters: { id: string; location: Point; count: number }[];
    }>(`catalog/map?${params}`)
      .then((data) => {
        if (cancelled) return;
        setCatalogCount(data.total);
        for (const cluster of data.clusters) {
          const el = textElement('span', cluster.count.toLocaleString('de-DE'));
          L.marker(cluster.location, {
            title: `${cluster.count} Katalogstandorte`,
            icon: L.divIcon({
              className: 'catalog-cluster',
              html: el,
              iconSize: [38, 38],
              iconAnchor: [19, 19],
            }),
            zIndexOffset: -300,
          })
            .on('click', () => m.setView(cluster.location, Math.min(16, m.getZoom() + 2)))
            .addTo(layer);
        }
        for (const entry of data.entries) {
          L.circleMarker(entry.location, {
            radius: 6,
            color: entry.hvo
              ? '#f0c15b'
              : entry.service === 'Polizei'
                ? '#65ade1'
                : entry.service === 'Feuerwehr'
                  ? '#e66a79'
                  : '#67c4a1',
            weight: 1.5,
            fillColor: '#11212c',
            fillOpacity: 0.95,
          })
            .bindTooltip(textElement('span', `${entry.name} · ${entry.service}`))
            .on('click', () => catalogSelectRef.current(entry))
            .addTo(layer);
        }
      })
      .catch((e) => {
        if (!cancelled) setCatalogError(e.message);
      });
    return () => {
      cancelled = true;
      layer.clearLayers();
    };
  }, [catalogVisible, bounds]);
  function locate() {
    const points = state.incidents.filter(isOpen).map((i) => i.location);
    if (points.length) map.current?.fitBounds(L.latLngBounds(points).pad(0.45), { maxZoom: 14 });
    else map.current?.setView(state.area.center, 12);
  }
  function locateArea() {
    if (!map.current) return;
    if (state.area.boundary?.length)
      map.current.fitBounds(L.latLngBounds(state.area.boundary.flat()), {
        padding: [28, 28],
        maxZoom: 11,
      });
    else map.current.setView(state.area.center, 12);
  }
  return (
    <div className={`map-shell ${mode === 'aerial' ? 'map-aerial' : 'map-street'}`}>
      <div ref={host} className="map-canvas" aria-label="Interaktive Einsatzkarte Deutschland" />
      <div className="map-top-note">
        <i />
        {mode === 'aerial' ? 'LUFTBILD · BADEN-WÜRTTEMBERG' : 'KARTE · DEUTSCHLAND'}
        {loading && <span>Wird geladen …</span>}
      </div>
      <div className="map-tools">
        <div className="compass">
          <span>N</span>
          <Navigation size={17} />
        </div>
        <button
          aria-label="Karte vergrößern"
          title="Vergrößern"
          onClick={() => map.current?.zoomIn()}
        >
          <Plus size={20} />
        </button>
        <button
          aria-label="Karte verkleinern"
          title="Verkleinern"
          onClick={() => map.current?.zoomOut()}
        >
          <Minus size={20} />
        </button>
        <span />
        <button
          aria-label="Alle Einsätze auf Karte anzeigen"
          title="Alle Einsätze anzeigen"
          onClick={locate}
        >
          <Crosshair size={19} />
        </button>
        <button
          aria-label="Kartenebenen"
          title="Kartenebenen"
          aria-expanded={layerMenu}
          onClick={() => setLayerMenu(!layerMenu)}
        >
          <Layers size={19} />
        </button>
        <button
          aria-label="Deutschlandkatalog auf Karte"
          title="Deutschlandkatalog einblenden"
          aria-pressed={catalogVisible}
          onClick={onCatalogToggle}
        >
          <Globe2 size={19} />
        </button>
        <button
          aria-label="Wachen auf Karte anzeigen"
          title="Wachen einblenden"
          aria-pressed={stationsVisible}
          onClick={() => setStationsVisible(!stationsVisible)}
        >
          <Building2 size={19} />
        </button>
      </div>
      {layerMenu && (
        <div className="layer-menu">
          <h3>Kartenebenen</h3>
          <label>
            <input
              type="checkbox"
              checked={stationsVisible}
              onChange={(e) => setStationsVisible(e.target.checked)}
            />
            Wachen anzeigen
          </label>
          <label>
            <input
              type="checkbox"
              checked={routesVisible}
              onChange={(e) => setRoutesVisible(e.target.checked)}
            />
            Anfahrten anzeigen
          </label>
          <p>Straßenrouten · ETA ohne Live-Verkehr.</p>
        </div>
      )}
      <div className="map-bottom-controls">
        <button
          className="map-locate"
          aria-label="Einsatzgebiet zentrieren"
          title="Einsatzgebiet zentrieren"
          onClick={locateArea}
        >
          <Navigation size={19} />
        </button>
        <div className="map-mode-switch" aria-label="Kartenansicht">
          <button
            className={mode === 'street' ? 'selected' : ''}
            aria-pressed={mode === 'street'}
            onClick={() => setMode('street')}
          >
            Karte
          </button>
          <button
            className={mode === 'aerial' ? 'selected' : ''}
            aria-pressed={mode === 'aerial'}
            onClick={() => setMode('aerial')}
          >
            Luftbild
          </button>
        </div>
        <span className="route-hint">
          <Route size={12} />
          Straßenrouten · ETA geschätzt
        </span>
      </div>
      {catalogVisible && (
        <div className="catalog-map-note">
          {catalogError
            ? 'Katalog nicht erreichbar'
            : `${catalogCount.toLocaleString('de-DE')} kartierte Standorte im Ausschnitt`}
          <small>© OpenStreetMap-Mitwirkende · ODbL · zum Auswählen hineinzoomen</small>
        </div>
      )}
      {failure && (
        <div className="map-error">
          {mode === 'aerial'
            ? 'Luftbilder sind derzeit nicht erreichbar.'
            : 'Kartenkacheln sind derzeit nicht erreichbar.'}
          <button onClick={() => setMode(mode === 'aerial' ? 'street' : 'aerial')}>
            Andere Kartenansicht öffnen
          </button>
        </div>
      )}
    </div>
  );
}
